<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import { Event, SportStatInputType } from '@collector/sportsapi-client-legacy'
import { getParticipantButtonColor } from '@mobile/components/Button/ButtonUtils'
import { ProbableLinkedParticipant } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import TeamSelect from '../../Popup/TeamSelect.vue'
import { useEventInjections } from '../../RelationDependencies/useEventInjections'
import { useEventPreparationEventStatsFactory } from './factories/EventPreparationEventStatsFactory/useEventPreparationEventStatsFactory'

export default defineComponent({
  components: { TeamSelect },
  props: {
    event: {
      type: Object as PropType<Event>,
      required: true,
    },
    setFullHeight: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['participantStatsUpdated'],
  setup(props) {
    const {
      sport,
      probableLinkedHomeParticipant,
      probableLinkedAwayParticipant,
    } = useEventInjections()
    const stats = useEventPreparationEventStatsFactory(
      props.event,
      sport.value,
    ).getStats()

    const homeParticipantColor = computed(() =>
      getParticipantButtonColor(
        probableLinkedHomeParticipant.getParticipant().value,
      ),
    )
    const awayParticipantColor = computed(() =>
      getParticipantButtonColor(
        probableLinkedAwayParticipant.getParticipant().value,
      ),
    )

    function getDefaultParticipant(
      statId: number,
    ): ProbableLinkedParticipant | null {
      const probableLinkedParticipants = [
        probableLinkedHomeParticipant,
        probableLinkedAwayParticipant,
      ]
      const defaultParticipant =
        probableLinkedParticipants.find((linkedParticipant) =>
          linkedParticipant
            .getParticipant()
            .value.stats.some((stat) => stat.id === statId && stat.value === 1),
        ) ?? null

      return defaultParticipant
    }

    function isStatAvailable(statId: number): boolean {
      return (
        probableLinkedHomeParticipant
          .getParticipant()
          .value.stats.some((stat) => stat.id === statId) &&
        probableLinkedAwayParticipant
          .getParticipant()
          .value.stats.some((stat) => stat.id === statId)
      )
    }

    return {
      stats,
      isStatAvailable,
      SportStatInputType,
      homeParticipantColor,
      awayParticipantColor,
      getDefaultParticipant,
    }
  },
})
</script>

<template>
  <div :class="setFullHeight ? 'h-full pb-3' : ''">
    <template
      v-for="(stat, index) in stats"
      :key="`stat-${index}`"
    >
      <div
        v-if="isStatAvailable(stat.id)"
        class="flex w-full items-center pb-3"
      >
        <div class="text-gray w-1/4 text-left text-xs font-bold">
          {{ stat.name }}
        </div>
        <div class="flex w-3/4">
          <template v-if="stat.type === SportStatInputType.SelectParticipant">
            <div class="justify-items flex w-full flex-col items-stretch">
              <TeamSelect
                :withTitle="false"
                :withButtons="false"
                :defaultParticipant="getDefaultParticipant(stat.id)"
                @participantSelected="
                  $emit('participantStatsUpdated', [
                    $event,
                    { id: stat.id, value: 1 },
                  ])
                "
              />
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
